import { hiDPI, size } from "polished";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import GBFlag from "../img/flags/gb.svg?react";
import SEFlag from "../img/flags/se.svg?react";
import headerAsset1x from "../img/header-asset@1x.png";
import headerAsset2x from "../img/header-asset@2x.png";
import logoFantasy1x from "../img/logo-fantasy@1x.png";
import logoFantasy1xWebp from "../img/logo-fantasy@1x.webp";
import logoFantasy2x from "../img/logo-fantasy@2x.png";
import logoFantasy2xWebp from "../img/logo-fantasy@2x.webp";
import Navigation from "./nav/Navigation";

const LogoFlagWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLink = styled.a`
  display: inline-block;
  vertical-align: middle;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    margin: 1rem 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    margin: 3rem 0;
  }
`;

const GameHeaderOuter = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: ${(props) => props.theme.colors.blueDarker3};
  background-repeat: no-repeat;
  background-position: 220px;
  background-size: cover;
  background-image: url(${headerAsset1x});

  ${hiDPI(2)} {
    background-image: url(${headerAsset2x});
  }

  @media (min-width: ${(props) => props.theme.breakpoints[4]}) {
    background-position: right;
    background-size: contain;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    position: relative;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.space[4]};
  max-width: 1220px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    display: block;
  }
`;

const FlagList = styled.ul`
  width: 34px;
  padding: 2px 2px 1px;
  border-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.white};
  list-style-type: none;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: auto;
    padding: 6px 4px 4px;
  }
`;

const FlagItem = styled.li`
  display: inline-block;
  vertical-align: middle;
  padding: ${({ theme }) => theme.space[1]};
  line-height: 1;

  &:last-child {
    padding-bottom: 3px;
  }

  &:not(:last-child) {
    border-bottom: ${({ theme }) => theme.borders[0]};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    &:last-child {
      padding-bottom: 0;
    }

    &:not(:last-child) {
      border-bottom: 0;
      border-right: ${({ theme }) => theme.borders[0]};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
      padding: 0 ${({ theme }) => theme.space[2]};
    }
  }
`;

const FlagLink = styled.a`
  display: inline-block;
  vertical-align: middle;
  line-height: 1;
`;

const StyledNOFlag = styled(SEFlag)`
  ${size(14, 20)}

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(22, 30)}
  }
`;

const StyledGBFlag = styled(GBFlag)`
  ${size(14, 20)}

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(22, 30)}
  }
`;

const Logo = styled.img`
  max-width: 120px;
  height: auto;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    max-width: 180px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    max-width: 287px;
    height: 65px;
  }
`;

const BorderBottom = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 0;
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.lighterBlue};
`;

const GameHeader = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  /**
   * Dynamically calculates header height and sets it as a CSS custom property.
   * This allows other components (MobileMenu, MobileMenuOverlay) to position
   * themselves correctly below the header at any screen size.
   */
  useEffect(() => {
    const updateHeaderHeight = () => {
      const height = headerRef.current?.offsetHeight;
      if (height) {
        document.documentElement.style.setProperty(
          "--header-height",
          `${height}px`,
        );
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  return (
    <GameHeaderOuter ref={headerRef}>
      <Header>
        <LogoFlagWrap>
          <HeaderLink
            href="https://allsvenskan.se/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <picture>
              <source
                type="image/webp"
                srcSet={`${logoFantasy1xWebp}, ${logoFantasy2xWebp} 2x`}
              />
              <Logo
                srcSet={`${logoFantasy1x}, ${logoFantasy2x} 2x`}
                src={logoFantasy1x}
                alt="Allsvenskan Fantasy"
              />
            </picture>
          </HeaderLink>
          <FlagList>
            <FlagItem>
              <FlagLink href="https://fantasy.allsvenskan.se">
                <StyledNOFlag />
              </FlagLink>
            </FlagItem>
            <FlagItem>
              <FlagLink href="https://en.fantasy.allsvenskan.se">
                <StyledGBFlag />
              </FlagLink>
            </FlagItem>
          </FlagList>
        </LogoFlagWrap>
        <Navigation />
      </Header>
      <BorderBottom />
    </GameHeaderOuter>
  );
};

export default GameHeader;
